import { useEffect, useCallback } from "react"

export interface LightboxImage {
  url: string
  name?: string
}

interface Props {
  images: LightboxImage[]
  index: number
  onIndexChange: (index: number) => void
  onClose: () => void
}

// 이미지 라이트박스: 같은 화면 팝업으로 이미지 확대 표시
// - 외부(배경) 클릭 또는 ESC 로 닫기
// - 좌우 화살표 / 키보드 ←→ 로 이전/다음 이동
export function ImageLightbox({ images, index, onIndexChange, onClose }: Props) {
  const total = images.length
  const hasMultiple = total > 1

  const goPrev = useCallback(() => {
    onIndexChange((index - 1 + total) % total)
  }, [index, total, onIndexChange])

  const goNext = useCallback(() => {
    onIndexChange((index + 1) % total)
  }, [index, total, onIndexChange])

  // 키보드 조작 (ESC 닫기, ← → 넘김) + body 스크롤 잠금
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
      else if (e.key === "ArrowLeft" && hasMultiple) goPrev()
      else if (e.key === "ArrowRight" && hasMultiple) goNext()
    }
    window.addEventListener("keydown", handleKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      window.removeEventListener("keydown", handleKey)
      document.body.style.overflow = prevOverflow
    }
  }, [onClose, goPrev, goNext, hasMultiple])

  const current = images[index]
  if (!current) return null

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
    >
      {/* 닫기 버튼 */}
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
        aria-label="닫기"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* 이전 버튼 */}
      {hasMultiple && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            goPrev()
          }}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
          aria-label="이전 이미지"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}

      {/* 다음 버튼 */}
      {hasMultiple && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            goNext()
          }}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
          aria-label="다음 이미지"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}

      {/* 이미지 본체 (클릭 시 닫힘 방지) */}
      <div
        className="max-w-[90vw] max-h-[85vh] flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={current.url}
          alt={current.name || "이미지"}
          className="max-w-[90vw] max-h-[78vh] object-contain rounded-lg shadow-2xl"
        />
        <div className="mt-3 flex items-center gap-3 text-white/80 text-sm shrink-0">
          {current.name && <span className="truncate max-w-[60vw]">{current.name}</span>}
          {hasMultiple && (
            <span className="tabular-nums text-white/60">
              {index + 1} / {total}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
